// src/tracking/gtm/services/unified-event-dispatcher.ts
import { logger } from '@/utils/logger';
import { EventName } from '@/types/events';
import { generateEventId } from '@/utils/tracking';
import { 
  UnifiedEventParams, 
  BaseEventParams, 
  VideoEventParams, 
  SurveyEventParams, 
  AppointmentEventParams,
  PageType 
} from '../../types/event-types';
import { EVENT_PARAMS } from '../config/gtm-config';

class UnifiedEventDispatcher {
  private static instance: UnifiedEventDispatcher;
  private lastEventTime: Record<string, number> = {};
  private eventCount: Record<string, number> = {};
  private readonly DEBOUNCE_TIME = 2000;
  private readonly PAGE_VIEW_DEBOUNCE = 5000;
  private readonly VIDEO_PROGRESS_DEBOUNCE = 1000;
  private readonly SURVEY_DEBOUNCE = 500;
  private readonly MAX_EVENT_COUNT = 1;

  private processedEvents: Set<string> = new Set();
  private eventSequence: string[] = [
    '0.01_UNIFIED_BASE_TAG',
    '0.02_EVENT_DISTRIBUTOR',
    '0.03_CAPI_HANDLER_TAG',
    '0.04_VSL_CORE_EVENTS',
    '0.05_CONVERSION_TRACKING_SYSTEM'
  ];

  private constructor() {
    window.pageViewCount = window.pageViewCount || 0;
    window.dataLayer = window.dataLayer || [];
    
    // Initialize GTM with standardized base tag
    if (!this.processedEvents.has('0.01_UNIFIED_BASE_TAG')) {
      window.dataLayer.push({
        event: '0.01_UNIFIED_BASE_TAG',
        detail: {
          eventParams: {
            ...EVENT_PARAMS.BASE,
            event_id: generateEventId(),
            timestamp: Date.now().toString(),
            event_sequence: '0.01'
          }
        }
      });
      this.processedEvents.add('0.01_UNIFIED_BASE_TAG');
    }
  }

  private validateEventSequence(eventName: string): boolean {
    // Allow non-system events to pass through
    if (!eventName.startsWith('0.')) {
      return true;
    }

    const eventIndex = this.eventSequence.indexOf(eventName);
    if (eventIndex === -1) {
      return false;
    }

    // Check if all previous events in sequence have been processed
    for (let i = 0; i < eventIndex; i++) {
      if (!this.processedEvents.has(this.eventSequence[i])) {
        logger.warn(`Event ${eventName} received before ${this.eventSequence[i]}`);
        return false;
      }
    }

    return true;
  }

  public static getInstance(): UnifiedEventDispatcher {
    if (!UnifiedEventDispatcher.instance) {
      UnifiedEventDispatcher.instance = new UnifiedEventDispatcher();
    }
    return UnifiedEventDispatcher.instance;
  }

  private shouldDebounce(
    eventName: string, 
    now: number, 
    params?: {
      eventParams?: UnifiedEventParams['eventParams'];
      detail?: {
        eventParams?: {
          is_milestone?: boolean;
        } & UnifiedEventParams['eventParams'];
      };
      [key: string]: unknown;
    }
  ): boolean {
    // Prevent re-triggering of system events
    if (eventName.startsWith('0.') && this.processedEvents.has(eventName)) {
      return true;
    }

    // Validate event sequence for system events
    if (eventName.startsWith('0.') && !this.validateEventSequence(eventName)) {
      return true;
    }
    const lastTime = this.lastEventTime[eventName];
    const currentCount = this.eventCount[eventName] || 0;

    // Never debounce milestone events or video start/complete events
    if (params?.detail?.eventParams?.is_milestone || 
        eventName === 'VIDEO_START' || 
        eventName === 'VIDEO_COMPLETE') {
      return false;
    }

    // Determine debounce time based on event type
    let debounceTime = this.DEBOUNCE_TIME;
    if (eventName === 'PageView') {
      debounceTime = this.PAGE_VIEW_DEBOUNCE;
    } else if (eventName.includes('VIDEO_PROGRESS')) {
      debounceTime = this.VIDEO_PROGRESS_DEBOUNCE;
    } else if (eventName.startsWith('SURVEY_')) {
      debounceTime = this.SURVEY_DEBOUNCE;
    }

    if (lastTime && (now - lastTime) < debounceTime) {
    if (eventName === 'PageView') {
      logger.info(`Page view event debounced`, {
        lastTime, now, timeDiff: now - lastTime
      });
    }
    if (currentCount >= this.MAX_EVENT_COUNT) {
        // Only log non-video progress events to reduce noise
        if (!eventName.includes('VIDEO_PROGRESS')) {
          logger.info(`Event '${eventName}' debounced - max count reached`, {
            eventName, lastTime, now, count: currentCount
          });
        }
        return true;
      }
      this.eventCount[eventName] = currentCount + 1;
    } else {
      this.eventCount[eventName] = 1;
    }

    this.lastEventTime[eventName] = now;
    return false;
  }

  public pushUnifiedEvent(
    eventName: EventName,
    params?: {
      eventParams?: UnifiedEventParams['eventParams'];
      userParams?: {
        user_id?: string;
        email?: string;
        phone?: string;
        first_name?: string;
        last_name?: string;
        city?: string;
        region?: string;
        postal_code?: string;
        country?: string;
      };
      [key: string]: unknown;
    },
    initState?: { ga4: boolean; fb: boolean; cf?: boolean; supabase?: boolean; gtmReady?: boolean }
  ): void {
    if (!window.dataLayer || !initState?.gtmReady || !window.EVENT_CONFIG?.GTM_CONFIG) return;

    const now = Date.now();
    if (this.shouldDebounce(eventName, now, params)) return;

    // Build event params based on event type
    let standardParams = { ...EVENT_PARAMS.BASE };
    
    if (eventName.startsWith('VIDEO_')) {
      standardParams = { ...standardParams, ...EVENT_PARAMS.VIDEO };
    } else if (eventName.startsWith('SURVEY_')) {
      standardParams = { ...standardParams, ...EVENT_PARAMS.SURVEY };
    } else if (eventName === 'APPOINTMENT_BOOKED') {
      standardParams = { ...standardParams, ...EVENT_PARAMS.APPOINTMENT };
    }

    const eventParams: BaseEventParams = {
      ...standardParams,
      ...(params?.eventParams || {}),
      event_id: generateEventId(),
      timestamp: now.toString(),
      event_name: eventName,
      page_type: (params?.eventParams?.page_type || 'standard') as PageType
    };

    if (eventName === 'PageView') {
      eventParams.page_source = document.referrer || 'direct';
      eventParams.page_url = window.location.href;
    }

    // Type guard to ensure proper event parameters
    const typedEventParams = (() => {
      if (eventName.startsWith('VIDEO_')) {
        return eventParams as VideoEventParams;
      } else if (eventName.startsWith('SURVEY_')) {
        return eventParams as SurveyEventParams;
      } else if (eventName === 'APPOINTMENT_BOOKED') {
        return eventParams as AppointmentEventParams;
      }
      return eventParams;
    })();

    const pushData = {
      event: eventName,
      ...(params || {}),
      detail: {
        eventParams: {
          ...typedEventParams,
          event_sequence: eventName.startsWith('0.') ? eventName.split('_')[0] : undefined
        },
        ...(params?.userParams && { userParams: params.userParams })
      }
    };

    // Push event through EVENT_DISTRIBUTOR if not already processed
    if (!this.processedEvents.has('0.02_EVENT_DISTRIBUTOR')) {
      window.dataLayer.push({
        event: '0.02_EVENT_DISTRIBUTOR',
        originalEvent: pushData
      });
      this.processedEvents.add('0.02_EVENT_DISTRIBUTOR');
    }

    logger.info(`GTM event pushed: ${eventName}`, pushData);

    // Handle CAPI events if FB is initialized and not already processed
    if (initState?.fb && pushData.detail && !this.processedEvents.has('0.03_CAPI_HANDLER_TAG')) {
      window.dataLayer.push({
        event: '0.03_CAPI_HANDLER_TAG',
        detail: pushData.detail
      });
      this.processedEvents.add('0.03_CAPI_HANDLER_TAG');
    }

    // Track processed events
    if (eventName.startsWith('0.')) {
      this.processedEvents.add(eventName);
    }
  }
}

export const unifiedEventDispatcher = UnifiedEventDispatcher.getInstance();
